import { useEffect, useMemo, useState } from 'react'
import { NavLink, Outlet, useParams } from 'react-router-dom'

import { getApplication } from '../../api/applications.js'
import { useAuth } from '../../context/AuthContext.jsx'

const ALL_SECTION_LINKS = [
  { to: 'configs', label: 'Group Configs', permission: null },
  { to: 'config-versions', label: 'Config Versions', permission: null },
  { to: 'elements', label: 'Elements', permission: null },
  { to: 'ab-tests', label: 'AB tests', permission: null },
  { to: 'segments', label: 'Segments', permission: null },
  { to: 'dynamic-rules', label: 'Dynamic Rules', permission: null },
  { to: 'scheduler', label: 'Scheduler', permission: 'read_scheduler' },
  { to: 'force-update', label: 'Force update', permission: 'force_updater' },
  { to: 'validator', label: 'Validator', permission: 'validator' },
  { to: 'config-tester', label: 'Config Tester', permission: 'qa_features' },
  { to: 'settings', label: 'Settings', permission: null },
]

export default function ApplicationSettingsLayoutPage() {
  const { id } = useParams()
  const hasValidId = Boolean(id)
  const { hasPermission } = useAuth()
  const [state, setState] = useState({
    status: 'loading',
    data: null,
    error: null,
  })

  useEffect(() => {
    if (!hasValidId) return undefined

    let cancelled = false

    getApplication(id)
      .then((data) => {
        if (!cancelled) {
          setState({ status: 'ok', data, error: null })
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: 'error', data: null, error })
        }
      })

    return () => {
      cancelled = true
    }
  }, [hasValidId, id])

  const hasCurrentData = state.status === 'ok' && state.data && String(state.data.id) === String(id)
  const application = hasCurrentData ? state.data : null

  const sectionLinks = useMemo(
    () => ALL_SECTION_LINKS.filter((s) => !s.permission || hasPermission(s.permission)),
    [hasPermission],
  )

  function handleApplicationUpdated(updatedApplication) {
    if (!updatedApplication) return
    setState({ status: 'ok', data: updatedApplication, error: null })
  }

  if (!hasValidId) {
    return (
      <div className="page">
        <h1 className="pageTitle">Application settings</h1>
        <pre className="codeBlock codeBlockError">Application id is required</pre>
      </div>
    )
  }

  return (
    <div className="page">
      {!application && state.status !== 'error' && <div className="muted">Loading application...</div>}
      {state.status === 'error' && (
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      )}

      {application ? (
        <>
          <nav className="sectionTabs" aria-label="Application sections">
            {sectionLinks.map((section) => (
              <NavLink
                key={section.to}
                to={`/applications/${id}/${section.to}`}
                className={({ isActive }) => `sectionTab${isActive ? ' sectionTabActive' : ''}`}
              >
                {section.label}
              </NavLink>
            ))}
          </nav>

          <Outlet context={{ application, onApplicationUpdated: handleApplicationUpdated }} />
        </>
      ) : null}
    </div>
  )
}
